'use client'
import clsx from 'clsx'
import { MouseEvent, useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import { useParams, useRouter } from 'next/navigation'
import Image from 'next/image'
import LifecycleLink from '@/components/common/LifecycleLink'
import useSWRInfinite from 'swr/infinite'
import { getUserChattedList, getUserChattedListKey } from 'Apis/chat'
import { filterVisibleChats, flattenChattedListPages } from 'Utils/chat'
import useInfiniteScroll from 'Hooks/useInfiniteScroll'
import { ChattedCharacterListRes, UserChattedListReq } from 'Types/character'
import { Popover } from 'antd'
import CharacterWinnerBadgeIcon, {
  CharacterWinnerBadgeGuard,
} from '@/components/common/CharacterWinnerBadgeIcon'
import { userState } from 'Store/user'
import { useRecoilValue } from 'recoil'
import { transformImage } from 'Utils/img'
import { SMALL_IMG_WIDTH } from '@/constant/img'
import { homeNSFWState } from 'Store/home'
import RecentTalkCharacterProfileCard from './RecentTalkCharacterProfileCard'
import DeleteChatConfirmModal from './RecentTalkDeleteChatConfirmModal'
import { USER_CHATTED_LIST_LIMIT } from '@/constant/chat'
import useI18n from 'Hooks/useI18n'
import { APP_HOME_PATHNAME } from 'Types/common'
import { withLocale } from '@/locales'
import useNavigateSimulatorGame from '@/hooks/simulator-game/useNavigateSimulatorGame'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import {
  trackSimulatorCardClick,
  trackSimulatorCardExposure,
} from '@/utils/simulatorGameTracking'

const LIMIT = USER_CHATTED_LIST_LIMIT

export default function RecentTalk() {
  const { lang } = useI18n()
  const { trackEvent } = useQuickTracking()
  const params = useParams<{ charId: string }>()
  const characterId = params?.charId
  const router = useRouter()
  const { navigateToSimulatorGame, worldGameProfileModal } =
    useNavigateSimulatorGame()
  const { touchBottom, loadMoreRef } = useInfiniteScroll()
  const user = useRecoilValue(userState)
  const nsfw = useRecoilValue(homeNSFWState)
  const listRef = useRef<HTMLDivElement | null>(null)
  const simulatorExposureTrackedRef = useRef(new Set<string>())
  //第一次刚进来走这里，先替换这里的请求方式
  const { data, error, isValidating, size, setSize } = useSWRInfinite(
    user
      ? getUserChattedListKey({ size: LIMIT, language_code: lang })
      : () => undefined,
    ({ req }: { req: UserChattedListReq }) => getUserChattedList(req),
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error('getUserChattedList error:', err)
      },
    }
  )

  useEffect(() => {
    if (isValidating || error) return
    const lastPage = data?.[size - 1]

    if (touchBottom && lastPage?.has_more) {
      setSize((size) => size + 1).then()
    }
  }, [data, error, isValidating, setSize, size, touchBottom])

  const filteredChattedList = useMemo(() => {
    return filterVisibleChats(flattenChattedListPages(data))
  }, [data])

  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const hasSimulatorItem = filteredChattedList.some(
      (char) => char.item_type === 'game'
    )
    if (!hasSimulatorItem) return

    const simulatorItems = Array.from(
      list.querySelectorAll<HTMLElement>('[data-simulator-recent-talk="true"]')
    )
    if (!simulatorItems.length) return

    const trackExposure = (node: HTMLElement) => {
      const simulatorId = node.dataset.simulatorId
      if (
        !simulatorId ||
        simulatorExposureTrackedRef.current.has(simulatorId)
      ) {
        return
      }
      simulatorExposureTrackedRef.current.add(simulatorId)
      trackSimulatorCardExposure(trackEvent, {
        simulatorId,
        creatorId: node.dataset.creatorId,
        pageName: 'time_corridor',
      })
    }

    if (typeof IntersectionObserver === 'undefined') {
      simulatorItems.forEach(trackExposure)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          trackExposure(entry.target as HTMLElement)
          observer.unobserve(entry.target)
        })
      },
      { root: list }
    )

    simulatorItems.forEach((node) => {
      if (
        !simulatorExposureTrackedRef.current.has(node.dataset.simulatorId || '')
      ) {
        observer.observe(node)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [filteredChattedList, trackEvent])

  const nextChatId = useMemo(() => {
    // data不存在或者聊天不存在
    if (
      !filteredChattedList ||
      (filteredChattedList?.length ?? 0) === 0 ||
      !characterId
    )
      return null

    const curIndex = filteredChattedList.findIndex(
      (item) => item.item_id === characterId
    )
    // 优先返回后一个聊天, 不存在后一个时返回前一个, 都不存在是返回null
    if (curIndex === -1) return null
    if (filteredChattedList.length === 1) return null
    if (curIndex === filteredChattedList.length - 1)
      return filteredChattedList[curIndex - 1].item_type === 'story'
        ? 'story' + filteredChattedList[curIndex - 1].item_id
        : filteredChattedList[curIndex - 1].item_id
    return filteredChattedList[curIndex + 1].item_type === 'story'
      ? 'story' + filteredChattedList[curIndex + 1].item_id
      : filteredChattedList[curIndex + 1].item_id
  }, [filteredChattedList, characterId])

  const handleDeleteSuccess = (delChatId: string) => {
    // 如果删除的不是当前在聊的角色, 那就不用处理
    if (delChatId !== characterId) return

    // 如果删除的是当前角色, 有下一个角色备选的话, 跳到下一个角色; 否则跳到首页
    if (characterId && delChatId && nextChatId) {
      router.replace(withLocale(`/chat/${nextChatId}`, lang))
    } else {
      router.replace(withLocale(APP_HOME_PATHNAME, lang))
    }
  }

  const getRecentTalkHref = (char: ChattedCharacterListRes) => {
    if (char.item_type === 'game') {
      return withLocale(`/world/${char.game_id || char.item_id}`, lang)
    }
    if (char.item_type === 'story') {
      return withLocale('/chat/story' + char.item_id, lang)
    }
    return char?.content_type === 2
      ? withLocale('/custom-chat/' + char.item_id, lang)
      : withLocale('/chat/' + char.item_id, lang)
  }

  const handleRecentTalkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    char: ChattedCharacterListRes
  ) => {
    if (char.item_type !== 'game') return

    event.preventDefault()
    trackSimulatorCardClick(trackEvent, {
      simulatorId: char.game_id || char.item_id,
      creatorId: char.creator?.user_id,
      pageName: 'time_corridor',
    })
    void navigateToSimulatorGame({
      gameId: char.game_id || char.item_id,
      versionChange: char.version_change,
      mode: 'desktop',
    })
  }

  return (
    <div
      className={clsx(
        'ml-2.5 pr-[60px]',
        !filteredChattedList?.length || !user ? 'hidden' : ''
      )}
    >
      <div
        className={clsx(
          'fixed bottom-[28px] top-[68px] rounded-[10px] border border-tertiary-border bg-tertiary-bg shadow-[0_2px_4px_0_rgba(255,255,255,0.05)]'
        )}
      >
        <motion.div
          ref={listRef}
          transition={{
            duration: 0.4,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={clsx(
            'chatted-list-scrollbar flex h-full flex-col items-center gap-2 overflow-y-hidden py-2 pl-[7px] pr-[4px] [scrollbar-gutter:stable] hover:overflow-y-auto'
          )}
        >
          {(filteredChattedList || []).map((char) => (
            <div
              key={char.item_id}
              className="relative"
              data-simulator-recent-talk={
                char.item_type === 'game' ? 'true' : undefined
              }
              data-simulator-id={
                char.item_type === 'game'
                  ? String(char.game_id || char.item_id || '')
                  : undefined
              }
              data-creator-id={
                char.item_type === 'game' ? char.creator?.user_id : undefined
              }
            >
              <div
                className="relative h-[40px] w-[40px] overflow-hidden rounded-3xl border border-primary-border transition-all duration-300 hover:rounded-lg hover:border-hover-border"
                style={{
                  borderRadius:
                    params.charId === char.item_id ? '8px' : undefined,
                }}
              >
                <Popover
                  zIndex={900}
                  content={<RecentTalkCharacterProfileCard character={char} />}
                  overlayInnerStyle={{
                    padding: 0,
                    boxShadow: 'unset',
                    backgroundColor: 'unset',
                    borderRadius: 'unset',
                  }}
                  arrow={false}
                  placement="rightTop"
                  align={{ offset: [7, 0] }}
                >
                  <LifecycleLink
                    href={getRecentTalkHref(char)}
                    prefetch
                    className="relative block h-10 w-10"
                    onClick={(event) => handleRecentTalkClick(event, char)}
                  >
                    <Image
                      unoptimized
                      className={clsx(
                        'object-cover',
                        'cursor-pointer',
                        !nsfw && char.nsfw && 'blur-[5px]'
                      )}
                      src={transformImage(char.face_url, SMALL_IMG_WIDTH)}
                      alt={char.nickname}
                      fill={true}
                    />
                  </LifecycleLink>
                </Popover>
              </div>
              <CharacterWinnerBadgeGuard badges={char.badges}>
                {() => (
                  <div className="absolute -right-0.5 bottom-0 h-[15px] w-[15px]">
                    <CharacterWinnerBadgeIcon
                      badges={char.badges}
                      className="h-full w-full"
                    />
                  </div>
                )}
              </CharacterWinnerBadgeGuard>
              {params.charId === char.item_id && (
                <motion.div
                  initial={{ height: 10, top: 15 }}
                  animate={{ height: 30, top: 5 }}
                  layoutId="rightLine"
                  className="absolute -left-1.5 top-[5px] z-10 h-[30px] w-[3px] rounded-r-lg bg-[#D9D9D9]"
                />
              )}
            </div>
          ))}
          <div ref={loadMoreRef} className="h-1" />
        </motion.div>
      </div>
      <DeleteChatConfirmModal onDelete={handleDeleteSuccess} />
      {worldGameProfileModal}
    </div>
  )
}
